/*
 * The client page's full width cards, built off Jobber's client page (owner shots, 2026-10-30): Work overview (every
 * request, quote, job and invoice), Billing (invoices and payments with the balance), Payment methods (the saved card), and
 * Client schedule (visits, with Mark done right there). Each pages through 10 at a time.
 */
import { useNavigate } from "@tanstack/react-router";
import { addDays, PAYMENT_METHOD_WORDS, type ClientDetail, type InvoiceRow, type PaymentListRow } from "@steward/shared";
import { useState, type ReactNode } from "react";
import {
  useAddReminder,
  useClientPricing,
  useClientReminders,
  useCompleteVisit,
  useJobs,
  useReopenVisit,
  useSetReminder,
  useVisits,
} from "../api/hooks";
import { useCardLink, useClientQuotes, useClientRequests, useInvoices, usePayments } from "../api/money";
import { useSession } from "../app/session";
import { money, moneyExact, phoneText, shortDate, timeRange, todayIn } from "../components/format";
import { Icon, type IconName } from "../components/Icon";
import { InputText } from "../components/fields";
import { PlanOfferSend } from "../components/PlanOffer";
import { FilterPick, Menu, useToast } from "../components/overlays";
import { Avatar, Button, Card, Chip, IconButton, LoadError, Skeleton, StatusLabel } from "../components/parts";
import { errText, VisitStatus } from "../components/work";
import { InvoiceStatus } from "./Invoices";
import { QuoteTag } from "./Quotes";
import { RequestTag } from "./Requests";
import { PhoneCard } from "./PhoneCard";

export const PAGE = 10;
const dayIn = (iso: string, tz: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(iso));

/** "Showing 1-10 of 48" and back and next arrows, under a card's table. Nothing when it all fits. */
export function Pager({
  at,
  total,
  go,
  size = PAGE,
  labels = ["Newer", "Older"],
}: {
  at: number;
  total: number;
  go: (at: number) => void;
  size?: number;
  labels?: [string, string];
}) {
  if (total <= size) return null;
  return (
    <div className="card-foot pager">
      <span className="muted num">
        Showing {at + 1}-{Math.min(total, at + size)} of {total}
      </span>
      <span className="pager-arrows">
        <IconButton icon="chevronLeft" label={labels[0]} disabled={at === 0} onClick={() => go(Math.max(0, at - size))} />
        <IconButton icon="chevronRight" label={labels[1]} disabled={at + size >= total} onClick={() => go(at + size)} />
      </span>
    </div>
  );
}

/** The + in a card's head: a short menu of what to make for this client, in the Create card's colors. */
function AddMenu({ items }: { items: { label: string; icon: IconName; color: string; go: () => void }[] }) {
  if (!items.length) return null;
  return (
    <Menu
      align="right"
      button={({ toggle }) => <IconButton icon="plus" label="Add" className="iconbtn card-add" onClick={toggle} />}
    >
      {(close) =>
        items.map((x) => (
          <button
            key={x.label}
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              x.go();
            }}
          >
            <span style={{ color: x.color }}>
              <Icon name={x.icon} size={18} />
            </span>{" "}
            {x.label}
          </button>
        ))
      }
    </Menu>
  );
}

// Work overview --------------------------------------------------------------------------------------------------------

type Kind = "request" | "quote" | "job" | "invoice";
const KIND: Record<Kind, { words: string; icon: IconName; color: string }> = {
  request: { words: "Requests", icon: "requests", color: "var(--color-flow-request)" },
  quote: { words: "Quotes", icon: "quotes", color: "var(--color-flow-quote)" },
  job: { words: "Jobs", icon: "jobs", color: "var(--color-flow-job)" },
  invoice: { words: "Invoices", icon: "invoices", color: "var(--color-flow-invoice)" },
};

interface WorkLine {
  key: string;
  kind: Kind;
  title: string;
  sub: string;
  /** "Due on", "Next visit" and the like, over the date. */
  when: string;
  day: string;
  status: ReactNode;
  /** Still needs something (open request, quote out, job running, invoice owed): the Status Open filter. */
  open: boolean;
  amount: ReactNode;
  go: () => void;
}

export function WorkOverview({ c }: { c: ClientDetail }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const tz = company.timezone;
  const [kind, setKind] = useState<Kind | "all">("all");
  const [state, setState] = useState<"all" | "open" | "closed">("all");
  const [at, setAt] = useState(0);
  const requests = useClientRequests(company.id, c.id, can("lead.read"));
  const quotes = useClientQuotes(company.id, c.id, can("quote.read"));
  const jobs = useJobs(company.id, { clientId: c.id, status: "all" }, can("job.read"));
  const invoices = useInvoices(company.id, { state: "all", clientId: c.id });
  const [offering, setOffering] = useState(false);
  // Offer a plan on a completed one-time home clean, while they have no schedule running (owner, 2026-11-02).
  const onPlan = (jobs.data?.items ?? []).some((j) => j.active && j.frequency !== "one-time");
  const offers = company.trade === "cleaning" && c.kind === "home" && can("quote.update") && !onPlan;
  const lines: WorkLine[] = [
    ...(requests.data?.requests ?? []).map((r): WorkLine => ({
      key: `r${r.id}`,
      kind: "request",
      title: "Request",
      sub: r.service ?? "Asked for work",
      when: "Asked on",
      day: dayIn(r.createdAt, tz),
      status: <RequestTag r={r} />,
      open: !r.archived && (r.state === "new" || r.state === "sent"),
      amount: r.valueCents ? money(r.valueCents) : null,
      go: () => void navigate({ to: "/requests/$id", params: { id: r.id } }),
    })),
    ...(quotes.data?.quotes ?? []).map((q): WorkLine => ({
      key: `q${q.id}`,
      kind: "quote",
      title: `Quote #${String(q.number)}`,
      sub: q.title ?? "Quote",
      when: q.sentAt ? "Sent on" : "Made on",
      day: dayIn(q.sentAt ?? q.createdAt, tz),
      status: <QuoteTag status={q.status} />,
      open: q.status === "draft" || q.status === "sent" || q.status === "approved",
      amount: moneyExact(q.totalCents),
      go: () => void navigate({ to: "/quotes/$id", params: { id: q.id } }),
    })),
    ...(can("job.read") ? (jobs.data?.items ?? []) : []).map((j): WorkLine => ({
      key: `j${j.id}`,
      kind: "job",
      title: j.number ? `Job #${String(j.number)}` : "Job",
      sub: j.name,
      when: j.completedOn ? "Done on" : !j.active ? "Ended on" : j.nextVisit ? "Next visit" : "Started on",
      day: j.completedOn ?? (!j.active ? j.endsOn : (j.nextVisit ?? j.startDate)) ?? "",
      status: j.completedOn ? (
        <span className="work-done">
          <StatusLabel status="success">Completed</StatusLabel>
          {offers ? (
            <button
              type="button"
              className="link small"
              onClick={(e) => {
                e.stopPropagation();
                setOffering(true);
              }}
            >
              Offer available
            </button>
          ) : null}
        </span>
      ) : j.active ? (
        <StatusLabel status="success">Active</StatusLabel>
      ) : (
        <StatusLabel status="inactive">Ended</StatusLabel>
      ),
      open: j.active && !j.completedOn,
      amount: (
        <>
          {moneyExact(j.visitTotalCents)}
          <small>{j.frequencyLabel}</small>
        </>
      ),
      go: () => void navigate({ to: "/jobs/$id", params: { id: j.id } }),
    })),
    ...(invoices.data?.invoices ?? []).map((i): WorkLine => ({
      key: `i${i.id}`,
      kind: "invoice",
      title: i.number ? `Invoice #${String(i.number)}` : "Invoice, not sent yet",
      sub: i.subject,
      when: i.dueAt && i.state !== "paid" && i.state !== "closed" ? "Due on" : "Made on",
      day: i.dueAt && i.state !== "paid" && i.state !== "closed" ? dayIn(i.dueAt, tz) : (i.issuedOn ?? i.firstServiceDate ?? ""),
      status: <InvoiceStatus i={i} />,
      open: i.state !== "paid" && i.state !== "closed",
      amount: (
        <>
          Total {moneyExact(i.totalCents)}
          <small>Balance {moneyExact(i.balanceCents)}</small>
        </>
      ),
      go: () => void navigate({ to: "/invoices/$id", params: { id: i.id } }),
    })),
  ].sort((a, b) => b.day.localeCompare(a.day) || a.key.localeCompare(b.key));
  const kinds = (Object.keys(KIND) as Kind[]).filter((k) => lines.some((l) => l.kind === k));
  const shown = lines.filter((l) => (kind === "all" || l.kind === kind) && (state === "all" || l.open === (state === "open")));
  const error = requests.error ?? quotes.error ?? jobs.error ?? invoices.error;
  const loading = requests.isLoading || quotes.isLoading || jobs.isLoading || invoices.isLoading;
  const pick = (k: Kind | "all") => {
    setKind(kind === k ? "all" : k);
    setAt(0);
  };
  const add = c.active
    ? [
        ...(can("quote.update")
          ? [
              {
                label: "Quote",
                ...KIND.quote,
                go: () => void navigate({ to: "/quotes/new", search: { clientId: c.id } }),
              },
            ]
          : []),
        ...(can("job.update")
          ? [{ label: "Job", ...KIND.job, go: () => void navigate({ to: "/jobs/new", search: { clientId: c.id } }) }]
          : []),
        ...(can("invoice.update")
          ? [
              {
                label: "Invoice",
                ...KIND.invoice,
                go: () => void navigate({ to: "/invoices", search: { new: true, clientId: c.id } }),
              },
            ]
          : []),
      ]
    : [];
  return (
    <Card title="Work overview" action={<AddMenu items={add} />} flush>
      {error ? (
        <LoadError error={error} />
      ) : loading ? (
        <Skeleton rows={4} />
      ) : lines.length ? (
        <>
          <div className="chips card-chips">
            <FilterPick
              label="Status"
              value={state}
              onChange={(v) => {
                setState(v);
                setAt(0);
              }}
              options={[
                { value: "all", label: "All" },
                { value: "open", label: "Open" },
                { value: "closed", label: "Closed" },
              ]}
            />
            {kinds.map((k) => (
              <Chip key={k} pressed={kind === k} onClick={() => pick(k)}>
                <span className="chip-icon" style={{ color: KIND[k].color }}>
                  <Icon name={KIND[k].icon} size={16} />
                </span>
                {KIND[k].words}
              </Chip>
            ))}
          </div>
          {shown.length ? (
            <table className="data work-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {shown.slice(at, at + PAGE).map((l) => (
                  <tr key={l.key} className="clickable" onClick={l.go}>
                    <td>
                      <span className="row-icon" style={{ color: KIND[l.kind].color }}>
                        <Icon name={KIND[l.kind].icon} size={18} />
                      </span>
                      <span className="two-line">
                        <b>{l.title}</b>
                        <small>{l.sub}</small>
                      </span>
                    </td>
                    <td className="num">
                      {l.day ? (
                        <span className="two-line">
                          <small>{l.when}</small>
                          {shortDate(l.day, 0)}
                        </span>
                      ) : (
                        <span className="muted">No date</span>
                      )}
                    </td>
                    <td>{l.status}</td>
                    <td className="right num">
                      <span className="two-line right">{l.amount}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="card-empty muted">Nothing here with these filters.</div>
          )}
          <Pager at={at} total={shown.length} go={setAt} />
        </>
      ) : (
        <div className="card-empty muted">No requests, quotes, jobs or invoices yet.</div>
      )}
      {offering ? <PlanOfferSend clientId={c.id} onClose={() => setOffering(false)} /> : null}
    </Card>
  );
}

// Billing --------------------------------------------------------------------------------------------------------------

/** One line of the Billing card: an invoice (adds to what they owe) or a payment (takes off; a refund adds back). */
interface BillLine {
  key: string;
  item: string;
  appliedTo: string;
  day: string;
  at: string;
  cents: number;
  tipCents: number;
  payment: boolean;
  open: () => void;
}

function invoiceLine(i: InvoiceRow, tz: string, open: () => void): BillLine {
  const left = i.paidCents > 0 && i.balanceCents > 0 ? `, ${moneyExact(i.balanceCents)} left` : "";
  const appliedTo =
    i.state === "paid"
      ? "Paid"
      : i.state === "closed"
        ? "Closed"
        : i.state === "debt"
          ? `Sent to debt${left}`
          : i.state === "past_due"
            ? `Past due${left}`
            : i.onTheWay
              ? "Payment on the way"
              : `${i.sentAt ? "Sent, waiting" : "Waiting"} for payment${left}`;
  const at = i.sentAt ?? `${i.issuedOn ?? i.firstServiceDate ?? ""}T12:00:00Z`;
  return {
    key: `i${i.id}`,
    item: i.number ? `Invoice #${String(i.number)}` : "Invoice",
    appliedTo,
    day: i.issuedOn ?? (i.sentAt ? dayIn(i.sentAt, tz) : (i.firstServiceDate ?? "")),
    at,
    cents: i.totalCents,
    tipCents: 0,
    payment: false,
    open,
  };
}

function paymentLine(p: PaymentListRow, tz: string, open: () => void): BillLine {
  const how = p.method === "crew" ? "to the crew" : PAYMENT_METHOD_WORDS[p.method].toLowerCase();
  const item = p.refund ? `Refund, ${how}` : `Payment, ${how}${p.online && p.method === "card" ? " online" : ""}`;
  const on = p.invoiceNumber ? `Invoice #${String(p.invoiceNumber)}` : p.invoiceId ? "Invoice" : "Account credit";
  return {
    key: `p${p.id}`,
    item,
    appliedTo: p.status === "pending" ? `${on}, on the way` : on,
    day: dayIn(p.receivedAt, tz),
    at: p.receivedAt,
    // Money in takes off what they owe; money given back adds to it.
    cents: -p.amountCents,
    tipCents: p.refund ? 0 : p.tipCents,
    payment: true,
    open,
  };
}

export function ClientBilling({ c }: { c: ClientDetail }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const [at, setAt] = useState(0);
  const invoices = useInvoices(company.id, { state: "all", clientId: c.id });
  const payments = usePayments(company.id, { clientId: c.id });
  const tz = company.timezone;
  const inv = (invoices.data?.invoices ?? []).filter((i) => i.state !== "to_check");
  const lines = [
    ...inv.map((i) => invoiceLine(i, tz, () => void navigate({ to: "/invoices/$id", params: { id: i.id } }))),
    ...(payments.data?.payments ?? [])
      .filter((p) => p.status !== "failed")
      .map((p) => paymentLine(p, tz, () => void navigate({ to: "/payments/$id", params: { id: p.id } }))),
  ].sort(
    (a, b) =>
      b.day.localeCompare(a.day) ||
      Number(b.payment) - Number(a.payment) ||
      b.at.localeCompare(a.at) ||
      a.key.localeCompare(b.key),
  );
  const owed = inv
    .filter((i) => i.state === "awaiting" || i.state === "past_due" || i.state === "debt")
    .reduce((s, i) => s + i.balanceCents, 0);
  const error = invoices.error ?? payments.error;
  const add =
    can("invoice.update") && c.active
      ? [
          {
            label: "New invoice",
            ...KIND.invoice,
            go: () => void navigate({ to: "/invoices", search: { new: true, clientId: c.id } }),
          },
        ]
      : [];
  return (
    <Card title="Billing" action={<AddMenu items={add} />} flush>
      {error ? (
        <LoadError
          error={error}
          retry={() => {
            void invoices.refetch();
            void payments.refetch();
          }}
        />
      ) : invoices.isLoading || payments.isLoading ? (
        <Skeleton rows={3} />
      ) : lines.length ? (
        <>
          <table className="data bill-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Applied to</th>
                <th>Date</th>
                <th className="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.slice(at, at + PAGE).map((l) => (
                <tr key={l.key} className="clickable" onClick={l.open}>
                  <td>
                    <span
                      className="row-icon"
                      style={{ color: l.payment ? "var(--color-flow-request)" : "var(--color-flow-job)" }}
                    >
                      <Icon name={l.payment ? "payments" : "invoices"} size={18} />
                    </span>
                    <b>{l.item}</b>
                  </td>
                  <td className="muted">{l.appliedTo}</td>
                  <td className="num">{shortDate(l.day, 0)}</td>
                  <td className="right num">
                    <span className="two-line right">
                      <b>{moneyExact(l.cents)}</b>
                      {l.tipCents > 0 ? <small>Includes {moneyExact(l.tipCents)} tip</small> : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {/* The empty second cell is the one phones hide (Applied to), so the amount stays under Amount. */}
              <tr>
                <td colSpan={2}>
                  <b>Current balance</b>
                </td>
                <td />
                <td className="right num">
                  <b className={owed > 0 ? "owed" : undefined}>{moneyExact(owed)}</b>
                </td>
              </tr>
            </tfoot>
          </table>
          <Pager at={at} total={lines.length} go={setAt} />
        </>
      ) : (
        <div className="card-empty muted">No invoices or payments yet.</div>
      )}
    </Card>
  );
}

// Payment methods ------------------------------------------------------------------------------------------------------

/** One line under Billing: the saved card and auto charge, or a way to ask for one (Jobber's Payment methods strip). */
export function PaymentMethods({ c }: { c: ClientDetail }) {
  const { can } = useSession();
  const toast = useToast();
  const cardLink = useCardLink();
  const [byPhone, setByPhone] = useState(false);
  const card = c.card;
  const old = card ? new Date(card.expYear, card.expMonth, 0) < new Date() : false;
  const ask = (send: boolean) =>
    cardLink.mutate(
      { clientId: c.id, send },
      {
        onSuccess: (r) =>
          send
            ? toast(`Card link texted to ${c.name}. Nothing is charged.`)
            : void navigator.clipboard.writeText(r.url).then(
                () => toast("Card link copied"),
                () => toast(r.url),
              ),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <section className="card pay-strip">
      <Icon name="payments" />
      <div className="pay-strip-text">
        {card ? (
          <>
            <b>
              {card.brand} ending in {card.last4}
            </b>{" "}
            <span className={old ? "owed" : "muted"}>
              {old ? "Expired" : "Expires"} {String(card.expMonth).padStart(2, "0")}/{String(card.expYear).slice(-2)}
            </span>{" "}
            {card.byPhone ? <span className="muted">Taken by phone</span> : null}{" "}
            {c.autoCharge ? (
              <StatusLabel status="success">Auto charge on</StatusLabel>
            ) : (
              <StatusLabel status="inactive">Auto charge off</StatusLabel>
            )}
          </>
        ) : (
          <>
            <b>Payment methods</b>{" "}
            <span className="muted">Once they save a card, it can pay their invoices with no one pressing anything.</span>
          </>
        )}
      </div>
      {can("invoice.update") && c.active ? (
        <Menu
          align="right"
          button={({ toggle }) => (
            <Button type="tertiary" size="small" onClick={toggle} loading={cardLink.isPending}>
              {card ? "Ask for a new card" : "Add or request"}
            </Button>
          )}
        >
          {(close) => (
            <>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  ask(true);
                }}
              >
                Text them a link to save a card
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  close();
                  ask(false);
                }}
              >
                Copy the link (to open it here with them)
              </button>
              {can("payment.record") ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    setByPhone(true);
                  }}
                >
                  Take card by phone
                </button>
              ) : null}
            </>
          )}
        </Menu>
      ) : null}
      {byPhone ? <PhoneCard c={c} onClose={() => setByPhone(false)} /> : null}
    </section>
  );
}

// Client schedule ------------------------------------------------------------------------------------------------------

interface ScheduleLine {
  key: string;
  day: string;
  time: string | null;
  title: string;
  sub: string | null;
  who: ReactNode;
  status: ReactNode;
  done: boolean;
  /** Upcoming and not done: Mark done shows. */
  canDone: boolean;
  late: boolean;
  type: "visit" | "reminder";
  go: () => void;
  markDone: () => void;
  /** The round check's words: Mark done, or where a Steward reminder's work is done. */
  doneWords?: string;
}

export function ClientSchedule({ c }: { c: ClientDetail }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const complete = useCompleteVisit();
  const reopen = useReopenVisit();
  const setReminder = useSetReminder();
  const today = todayIn(company.timezone);
  const [state, setState] = useState<"all" | "upcoming" | "done">("all");
  const [type, setType] = useState<"all" | "visit" | "reminder">("all");
  const [at, setAt] = useState(0);
  const [adding, setAdding] = useState(false);
  const reminders = useClientReminders(c.id, can("client.read"));
  // The server takes up to 400 days for one client: a year back and two months on.
  const visits = useVisits(
    company.id,
    { from: addDays(today, -330), to: addDays(today, 62), clientId: c.id },
    can("schedule.read"),
  );
  if (!can("schedule.read")) return null;
  const lines: ScheduleLine[] = [
    ...(visits.data?.visits ?? []).map((v): ScheduleLine => ({
      key: `v${v.ref}`,
      day: v.date,
      time: v.startTime,
      title: `Visit for ${v.jobNumber ? `Job #${String(v.jobNumber)}` : "a job"} · ${v.jobName}`,
      sub: v.moved ? `Moved from ${shortDate(v.originalDate, 0)}` : null,
      who: v.crew.length ? (
        <span className="who">
          <Avatar name={v.crew[0]!.name} person={v.crew[0]!} size="small" />
          {v.crew.map((p) => p.name.split(" ")[0]).join(", ")}
        </span>
      ) : (
        <span className="who unassigned">
          <Icon name="person" size={16} /> Unassigned
        </span>
      ),
      status: <VisitStatus status={v.status} late={v.date < today} />,
      done: v.status === "done" || v.status === "skipped" || v.status === "cancelled",
      canDone: can("visit.update") && (v.status === "scheduled" || v.status === "in_progress") && v.date <= today,
      late: v.date < today && v.status === "scheduled",
      type: "visit",
      go: () => void navigate({ to: "/visits/$ref", params: { ref: v.ref } }),
      markDone: () =>
        complete.mutate(
          { ref: v.ref },
          {
            onSuccess: (d) => toast(`Visit on ${shortDate(v.date, 0)} marked done`, { undo: () => reopen.mutate(d.ref) }),
            onError: (e) => toast(errText(e), { kind: "error" }),
          },
        ),
    })),
    ...(reminders.data ?? []).map((r): ScheduleLine => {
      const open = r.quoteId
        ? () => void navigate({ to: "/quotes/$id", params: { id: r.quoteId! } })
        : r.invoiceId
          ? () => void navigate({ to: "/invoices/$id", params: { id: r.invoiceId! } })
          : () => undefined;
      const flip = (done: boolean) =>
        setReminder.mutate(
          { clientId: c.id, id: r.id!, done },
          {
            onSuccess: () =>
              done
                ? toast("Reminder marked done", { undo: () => setReminder.mutate({ clientId: c.id, id: r.id!, done: false }) })
                : toast("Reminder is open again"),
            onError: (e) => toast(errText(e), { kind: "error" }),
          },
        );
      return {
        key: r.key,
        day: r.dueOn,
        time: r.dueTime,
        title: r.title,
        sub: r.sub,
        who: r.assignedName ? (
          <span className="who">
            <Avatar name={r.assignedName} size="small" />
            {r.assignedName.split(" ")[0]}
          </span>
        ) : (
          <span className="who unassigned">
            <Icon name="person" size={16} /> Unassigned
          </span>
        ),
        status: r.done ? (
          <StatusLabel status="success">Done</StatusLabel>
        ) : r.kind === "invoice" ? (
          <StatusLabel status="critical">Past due</StatusLabel>
        ) : r.dueOn < today ? (
          <StatusLabel status="warning">Overdue</StatusLabel>
        ) : (
          <StatusLabel status="informative">To do</StatusLabel>
        ),
        done: r.done,
        canDone: !r.done && (r.kind !== "task" || can("client.update")),
        late: !r.done && r.dueOn < today,
        type: "reminder",
        go: open,
        markDone: r.kind === "task" ? () => flip(true) : open,
        doneWords: r.kind === "quote" ? "Make the job" : r.kind === "invoice" ? "Open the invoice" : "Mark done",
      };
    }),
  ];
  // Upcoming first, soonest at the top; then the past, newest first (Jobber's order).
  const upcoming = lines.filter((l) => !l.done && l.day >= today).sort((a, b) => a.day.localeCompare(b.day));
  const past = lines
    .filter((l) => l.done || l.day < today)
    .sort((a, b) => b.day.localeCompare(a.day) || (b.time ?? "").localeCompare(a.time ?? ""));
  const late = past.filter((l) => l.late);
  const ordered = [...late, ...upcoming, ...past.filter((l) => !l.late)].filter(
    (l) => (state === "all" || (state === "done" ? l.done : !l.done)) && (type === "all" || l.type === type),
  );
  const error = visits.error ?? reminders.error;
  const add = [
    ...(c.active && can("job.update")
      ? [
          {
            label: "Job",
            ...KIND.job,
            go: () => void navigate({ to: "/jobs/new", search: { clientId: c.id } }),
          },
        ]
      : []),
    ...(can("client.update")
      ? [{ label: "Reminder", icon: "bell" as IconName, color: "var(--color-text--secondary)", go: () => setAdding(true) }]
      : []),
  ];
  return (
    <Card title="Client schedule" action={<AddMenu items={add} />} flush>
      {adding ? <ReminderForm c={c} today={today} close={() => setAdding(false)} /> : null}
      {error ? (
        <LoadError error={error} />
      ) : visits.isLoading || reminders.isLoading ? (
        <Skeleton rows={4} />
      ) : lines.length ? (
        <>
          <div className="chips card-chips">
            <FilterPick
              label="Type"
              value={type}
              onChange={(v) => {
                setType(v);
                setAt(0);
              }}
              options={[
                { value: "all", label: "All" },
                { value: "visit", label: "Visits" },
                { value: "reminder", label: "Reminders" },
              ]}
            />
            <FilterPick
              label="Status"
              value={state}
              onChange={(v) => {
                setState(v);
                setAt(0);
              }}
              options={[
                { value: "all", label: "All" },
                { value: "upcoming", label: "Not done" },
                { value: "done", label: "Done" },
              ]}
            />
          </div>
          {ordered.length ? (
            <table className="data sched-table">
              <thead>
                <tr>
                  <th>Schedule</th>
                  <th>Title</th>
                  <th>Who</th>
                  <th>Status</th>
                  <th className="right">
                    <span className="sr-only">Mark done</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ordered.slice(at, at + PAGE).map((l) => (
                  <tr key={l.key} className={l.key.startsWith("t") ? undefined : "clickable"} onClick={l.go}>
                    <td className="num">
                      <span
                        className="row-icon"
                        style={{ color: l.type === "visit" ? "var(--color-flow-job)" : "var(--color-critical)" }}
                      >
                        <Icon name={l.type === "visit" ? "schedule" : "bell"} size={18} />
                      </span>
                      <b>
                        {shortDate(l.day, 0)}
                        {l.time ? `, ${timeRange(l.time, null)}` : ""}
                      </b>
                    </td>
                    <td>
                      <span className="two-line">
                        {l.title}
                        {l.sub ? <small>{l.sub}</small> : null}
                      </span>
                    </td>
                    <td>{l.who}</td>
                    <td>{l.status}</td>
                    <td className="right">
                      {l.canDone ? (
                        <button
                          type="button"
                          className="iconbtn done-btn"
                          aria-label={l.doneWords ?? "Mark done"}
                          data-tip={l.doneWords ?? "Mark done"}
                          disabled={complete.isPending || setReminder.isPending}
                          onClick={(e) => {
                            // The row opens the visit; this button only marks it done.
                            e.stopPropagation();
                            l.markDone();
                          }}
                        >
                          <Icon name="check" size={18} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="card-empty muted">Nothing here with these filters.</div>
          )}
          <Pager at={at} total={ordered.length} go={setAt} />
        </>
      ) : (
        <div className="card-empty muted">No visits or reminders in the past year or the next two months.</div>
      )}
    </Card>
  );
}

/** A reminder the office sets for itself on this client (Jobber's Reminder in the Client schedule's +). */
function ReminderForm({ c, today, close }: { c: ClientDetail; today: string; close: () => void }) {
  const add = useAddReminder();
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [day, setDay] = useState(addDays(today, 1));
  const [time, setTime] = useState("");
  return (
    <form
      className="note-form"
      onSubmit={(e) => {
        e.preventDefault();
        add.mutate(
          { clientId: c.id, reminder: { title: title.trim(), dueOn: day, dueTime: time || null } },
          {
            onSuccess: () => {
              toast("Reminder added");
              close();
            },
            onError: (er) => toast(errText(er), { kind: "error" }),
          },
        );
      }}
    >
      <InputText
        label="Remind me to"
        wide
        autoFocus
        value={title}
        placeholder={`Call ${c.firstName ?? c.name} about the spring clean`}
        onChange={(e) => setTitle(e.target.value)}
      />
      <InputText label="Day" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
      <InputText
        label="Time"
        type="time"
        value={time}
        description="Leave empty for any time"
        onChange={(e) => setTime(e.target.value)}
      />
      <div className="row end">
        <Button type="tertiary" size="small" onClick={close}>
          Cancel
        </Button>
        <Button size="small" submit loading={add.isPending} disabled={!title.trim() || !day}>
          Save
        </Button>
      </div>
    </form>
  );
}

// Recent pricing -------------------------------------------------------------------------------------------------------

/** Jobber's Recent pricing: what each service was last quoted at and what it costs on a job, so prices stay the same. */
export function RecentPricing({ c }: { c: ClientDetail }) {
  const { can } = useSession();
  const navigate = useNavigate();
  const pricing = useClientPricing(c.id, can("quote.read") || can("job.read"));
  if (!can("quote.read") && !can("job.read")) return null;
  const rows = pricing.data ?? [];
  return (
    <Card title="Recent pricing" flush>
      {pricing.error ? (
        <LoadError error={pricing.error} />
      ) : pricing.isLoading ? (
        <Skeleton rows={3} />
      ) : rows.length ? (
        <table className="data">
          <thead>
            <tr>
              <th>Line item</th>
              <th className="right">Quoted</th>
              <th className="right">Job</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.description}>
                <td>
                  <b>{r.description}</b>
                </td>
                <td className="right">
                  {r.quotedCents !== null ? (
                    <button
                      type="button"
                      className="link two-line right"
                      disabled={!r.quoteId}
                      onClick={() => (r.quoteId ? void navigate({ to: "/quotes/$id", params: { id: r.quoteId } }) : undefined)}
                    >
                      {moneyExact(r.quotedCents)}
                      {r.quotedOn ? <small>{shortDate(r.quotedOn)}</small> : null}
                    </button>
                  ) : (
                    <span className="muted">None</span>
                  )}
                </td>
                <td className="right">
                  {r.jobId && r.jobCents !== null ? (
                    <button
                      type="button"
                      className="link two-line right"
                      onClick={() => void navigate({ to: "/jobs/$id", params: { id: r.jobId! } })}
                    >
                      {moneyExact(r.jobCents)}
                      <small>
                        {[r.jobNumber ? `Job #${String(r.jobNumber)}` : null, r.jobOn ? shortDate(r.jobOn) : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    </button>
                  ) : (
                    <span className="muted">None</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="card-empty muted">Prices show here once you quote them or book a job.</div>
      )}
    </Card>
  );
}

// Header facts ---------------------------------------------------------------------------------------------------------

/** Jobber's facts under the client's name: how to reach them on the left, how they pay and who sent them on the right. */
export function ClientFacts({ c, leadWords }: { c: ClientDetail; leadWords: string | null }) {
  const navigate = useNavigate();
  const dash = <span className="muted">None</span>;
  return (
    <section className="client-facts">
      <dl>
        <dt>Main phone</dt>
        <dd>{c.phone ? <a href={`tel:${c.phone}`}>{phoneText(c.phone) ?? c.phone}</a> : dash}</dd>
        <dt>Main email</dt>
        <dd>{c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : dash}</dd>
        <dt>Billing address</dt>
        <dd>{c.billingAddress ?? <span className="muted">Same as the property</span>}</dd>
      </dl>
      <dl>
        <dt>Payment terms</dt>
        <dd>{c.paymentTerms ?? dash}</dd>
        <dt>Lead source</dt>
        <dd>{leadWords ?? dash}</dd>
        <dt>Referred by</dt>
        <dd>
          {c.referredBy ? (
            <button
              type="button"
              className="link"
              onClick={() => void navigate({ to: "/clients/$id", params: { id: c.referredBy!.id } })}
            >
              {c.referredBy.name}
            </button>
          ) : (
            dash
          )}
        </dd>
      </dl>
    </section>
  );
}

/** The header's Create: everything new for this client in one menu, with the Create card's colors (Jobber). */
export function CreateForClient({ c }: { c: ClientDetail }) {
  const { can } = useSession();
  const navigate = useNavigate();
  if (!c.active) return null;
  const items = [
    ...(can("quote.update")
      ? [{ label: "Quote", ...KIND.quote, go: () => void navigate({ to: "/quotes/new", search: { clientId: c.id } }) }]
      : []),
    ...(can("job.update")
      ? [{ label: "Job", ...KIND.job, go: () => void navigate({ to: "/jobs/new", search: { clientId: c.id } }) }]
      : []),
    ...(can("invoice.update")
      ? [
          {
            label: "Invoice",
            ...KIND.invoice,
            go: () => void navigate({ to: "/invoices", search: { new: true, clientId: c.id } }),
          },
        ]
      : []),
  ];
  if (!items.length) return null;
  return (
    <Menu
      align="right"
      button={({ toggle }) => (
        <Button icon="plus" onClick={toggle}>
          Create
        </Button>
      )}
    >
      {(close) =>
        items.map((x) => (
          <button
            key={x.label}
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              x.go();
            }}
          >
            <span style={{ color: x.color }}>
              <Icon name={x.icon} size={18} />
            </span>{" "}
            {x.label}
          </button>
        ))
      }
    </Menu>
  );
}
